import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef } from 'react';

import { useLocalStorage, useSessionStorage } from 'usehooks-ts';

import type { FmRecognitionState } from '@/ducks/fm/types';

import { FM_RECOGNITION_LAST_SESSION_KEY, FM_RECOGNITION_SESSIONS_KEY } from '@/ducks/fm/constants';
import { selectAgencyId, selectAgentId, selectFilterAgencyIsAvailable } from '@/ducks/fm/selectors';
import useVWOFeature from '@/hooks/useVWOFeature';
import { useAppSelector } from '@/store';
import { VWOFlag } from '@/types/vwo';

export const useFMRecognition = () => {
  const agentId = useAppSelector(selectAgentId);
  const agencyId = useAppSelector(selectAgencyId);

  const isFmFlowAvailable = useAppSelector(selectFilterAgencyIsAvailable);

  const searchParams = useSearchParams();

  const data = useFMRecognitionLogic({ agencyId, agentId, isFmFlowAvailable, searchParams });
  return data;
};

const defaultState: FmRecognitionState = { isLabelVisible: false, wasPopupShown: false };

const useFMRecognitionLogic = ({
  agencyId,
  agentId,
  isFmFlowAvailable,
  searchParams,
}: {
  agencyId: string;
  agentId: string;
  isFmFlowAvailable: boolean;
  searchParams: URLSearchParams;
}) => {
  const key = useRef(agentId && agencyId ? `${agentId}-${agencyId}` : '');
  const [lastSessionKey, setLastSessionKey] = useSessionStorage<string>(FM_RECOGNITION_LAST_SESSION_KEY, '');
  const changeLastSessionKey = useCallback(
    (key: string) => {
      if (key && key !== lastSessionKey) {
        setLastSessionKey(key);
      }
    },
    [lastSessionKey, setLastSessionKey],
  );
  const resetLastSessionKey = useCallback(() => {
    setLastSessionKey('');
  }, [setLastSessionKey]);

  useEffect(() => {
    changeLastSessionKey(key.current);
  }, []);

  const [fmRecognitionState, setFmRecognitionState] = useLocalStorage<Record<string, FmRecognitionState>>(
    FM_RECOGNITION_SESSIONS_KEY,
    lastSessionKey ? { [lastSessionKey]: defaultState } : {},
  );
  const value = lastSessionKey ? fmRecognitionState[lastSessionKey] : undefined;
  const setValue = useCallback(
    (value: FmRecognitionState) => {
      if (!lastSessionKey) return;

      setFmRecognitionState((currentValue) => ({ ...currentValue, [lastSessionKey]: value }));
    },
    [lastSessionKey, setFmRecognitionState],
  );

  const onPopupClosed = useCallback(() => {
    setValue({ isLabelVisible: true, wasPopupShown: true });
  }, [setValue]);

  const onReset = useCallback(() => {
    if (!value || isFmFlowAvailable) return;

    setValue({ ...value, isLabelVisible: false });
    resetLastSessionKey();
  }, [value, isFmFlowAvailable, resetLastSessionKey, setValue]);

  const outOfCharterFlow = !searchParams.get('charterId');

  const { isVWOLoading, value: isFmRecognitionFeatureEnabled } = useVWOFeature(VWOFlag.BOOK_FM_RECOGNITION);
  const isFmRecognitionFlowEnabled = !isVWOLoading && isFmRecognitionFeatureEnabled && outOfCharterFlow;

  const isLabelCanBeShown = isFmRecognitionFlowEnabled && Boolean(value?.isLabelVisible);
  const isPopupCanBeShown = isFmRecognitionFlowEnabled && isFmFlowAvailable && !value?.wasPopupShown;

  return {
    isFmFlowAvailable,
    isFmRecognitionFlowEnabled,
    isLabelCanBeShown,
    isPopupCanBeShown,
    onPopupClosed,
    onReset,
    value,
  };
};
